import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MicrosoftIntegrationService } from 'src/app/services/microsoft-integration.service';
import { FrontendLoggerService } from 'src/app/services/frontend-logger.service';

interface MicrosoftOAuthMessage {
  source: 'microsoft-oauth';
  status: 'success' | 'error';
  accountId?: string;
  email?: string;
  message?: string;
}

@Component({
  selector: 'app-microsoft-callback',
  templateUrl: './microsoft-callback.component.html',
  styleUrls: ['./microsoft-callback.component.scss'],
  standalone: false
})
export class MicrosoftCallbackComponent implements OnInit {
  readonly sessionExpiredMessage = 'Session expired. Please sign in again and reconnect Microsoft.';
  isLoading = true;
  errorMessage = '';
  successMessage = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly microsoftIntegrationService: MicrosoftIntegrationService,
    private readonly frontendLogger: FrontendLoggerService
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const code = params.get('code');
      const state = params.get('state') ?? undefined;
      const scope = params.get('scope') ?? undefined;
      const providerError = params.get('error') || params.get('error_description');
      const redirectUri = `${globalThis.location?.origin ?? ''}/oauth/microsoft/callback`;

      this.frontendLogger.info('[MicrosoftCallbackComponent] OAuth callback received.', { isPopup: !!globalThis.opener });

      if (providerError || !code) {
        this.failSafely();
        return;
      }

      this.microsoftIntegrationService.exchangeCodeForToken({ code, state, scope, redirectUri }).subscribe({
        next: (response) => {
          if (!response.success || !response.accountId) {
            this.failSafely();
            return;
          }

          this.isLoading = false;
          this.successMessage = response.message || 'Microsoft connection completed.';
          this.postToOpener({
            source: 'microsoft-oauth',
            status: 'success',
            accountId: response.accountId,
            ...(response.email ? { email: response.email } : {}),
          });
        },
        error: (error: unknown) => {
          const status = this.extractStatus(error);
          this.frontendLogger.warn('[MicrosoftCallbackComponent] callback exchange failed', { status });
          this.failSafely();
        }
      });
    });
  }

  private failSafely(): void {
    this.isLoading = false;
    this.errorMessage = this.sessionExpiredMessage;
    this.postToOpener({ source: 'microsoft-oauth', status: 'error', message: this.sessionExpiredMessage });
  }

  private postToOpener(payload: MicrosoftOAuthMessage): void {
    const origin = globalThis.location?.origin ?? '';
    if (globalThis.opener) {
      try {
        globalThis.opener.postMessage(payload, origin);
      } catch (error) {
        this.frontendLogger.error('[MicrosoftCallbackComponent] postMessage failed', String(error));
      }
      try {
        globalThis.close();
      } catch {
        // Browser may block close for manually opened tabs.
      }
      return;
    }

    if (payload.status === 'success') {
      this.router.navigate(['/admin/settings'], { queryParams: { provider: 'microsoft' } });
      return;
    }

    this.router.navigate(['/admin/settings'], { queryParams: { provider: 'microsoft', error: 'session-expired' } });
  }

  private extractStatus(error: unknown): string {
    if (typeof error === 'object' && error !== null && 'status' in error) {
      const status = (error as { status?: unknown }).status;
      return typeof status === 'string' || typeof status === 'number' ? String(status) : 'unknown';
    }

    return 'unknown';
  }
}
